import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.jsx";
import { Shell } from "../components/Shell.jsx";
import { ACTIVATION_PATHS, campaignBuilderRoute } from "../navConfig.js";

const GUIDE_STEPS = [
  {
    number: "01",
    phase: "Understand",
    title: "Review program enrollment",
    role: "HR manager",
    description:
      "Start with the Enrollment dashboard to compare eligible, enrolled, campaign-candidate, and guardrail-suppressed populations by program.",
    checks: [
      "Confirm the evidence period shown on each program card.",
      "Compare Goal 1 enrollment gaps with Goal 2 program-use opportunities.",
      "Use the information tooltips when a count or detection rule needs context.",
    ],
    href: ACTIVATION_PATHS.enrollment,
    action: "Open Enrollment",
  },
  {
    number: "02",
    phase: "Explore",
    title: "Validate the opportunity",
    role: "HR manager",
    description:
      "Open Opportunities to compare programs, stages, priorities, recommended actions, and aggregate audience sizes before selecting a campaign path.",
    checks: [
      "Filter to the intended program and evidence period.",
      "Review the recommended action—not the stage name alone.",
      "Treat suppression as a control; suppressed records are not routine outreach candidates.",
    ],
    href: ACTIVATION_PATHS.opportunities,
    action: "Explore Opportunities",
  },
  {
    number: "03",
    phase: "Define",
    title: "Choose the campaign goal",
    role: "HR manager",
    description:
      "Decide whether the campaign should encourage benefit enrollment or activate use among people already enrolled.",
    checks: [
      "Goal 1: eligible member is not enrolled and has a matching current opportunity.",
      "Goal 2: member is enrolled and has a recommended program-use action.",
      "Use one program, goal, and immutable evidence period per campaign version.",
    ],
  },
  {
    number: "04",
    phase: "Build",
    title: "Create the audience definition",
    role: "HR manager",
    description:
      "Create a campaign, select the program and evidence period, then refine the aggregate audience using supported cohort filters.",
    checks: [
      "Confirm the selected evidence period is the intended source snapshot.",
      "Review the aggregate cohort estimate and minimum privacy threshold.",
      "Do not create, paste, or export a manual member list.",
    ],
    href: campaignBuilderRoute({ source: "campaigns" }),
    action: "Create campaign",
  },
  {
    number: "05",
    phase: "Compose",
    title: "Plan Workday app messages",
    role: "HR manager",
    description:
      "Add each campaign touch, select an active approved Workday app template, and set its relative sequence day.",
    checks: [
      "Each Workday app touch requires approved message content.",
      "Keep required safety language and respect high-sensitivity messaging restrictions.",
      "Describe the benefit and next step without presenting claims evidence as a diagnosis.",
    ],
  },
  {
    number: "06",
    phase: "Govern",
    title: "Resolve every governance result",
    role: "HR manager",
    description:
      "Use the final builder step to review source quality, enrollment alignment, cohort privacy, guardrails, templates, content, sensitivity, cadence, and safety language.",
    checks: [
      "A failed governance check blocks progression.",
      "A warning requires review and an informed decision.",
      "Recheck the audience estimate and exact campaign version before submission.",
    ],
  },
  {
    number: "07",
    phase: "Review",
    title: "Submit for independent approval",
    role: "Manager + reviewer",
    description:
      "Submit the completed campaign for review. A different authorized user must assess and approve the exact version that was submitted.",
    checks: [
      "The campaign creator cannot approve their own version.",
      "Revisions create a new version that must be reviewed again.",
      "Governance runs again when the reviewer approves or rejects the campaign.",
    ],
    href: ACTIVATION_PATHS.campaigns,
    action: "Open Campaigns",
  },
  {
    number: "08",
    phase: "Publish",
    title: "Resolve contact conflicts and publish",
    role: "Reviewer + operator",
    description:
      "Approval applies spacing and frequency rules to reserve or hold planned touches. An authorized operator can then publish the approved campaign definition.",
    checks: [
      "Held touches can result from spacing, frequency, same-program, or higher-priority conflicts.",
      "Publication reruns governance and records the approved definition and audit event.",
      "Publishing does not currently create delivery jobs or send messages.",
    ],
  },
  {
    number: "09",
    phase: "Monitor",
    title: "Monitor source and opportunity trends",
    role: "HR team",
    description:
      "Use Monitoring to follow opportunity volumes, actionable and suppressed populations, priority mix, and evidence-period changes.",
    checks: [
      "Investigate unexpected suppression or population shifts before reusing a campaign strategy.",
      "Use aggregate trends; outcome attribution and verified delivery feedback are not yet connected.",
      "Return to the campaign workspace for definition, approval, and publication status.",
    ],
    href: ACTIVATION_PATHS.monitoring,
    action: "Open Monitoring",
  },
];

export function HowToGuide({ tenant, auth }) {
  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="how-to"
      title="How-to Guide"
      subtitle="A step-by-step workflow for creating and governing a campaign"
    >
      <section className="howto-hero">
        <div>
          <span className="howto-eyebrow">Recommended workflow</span>
          <h2>From opportunity insight to an approved campaign definition</h2>
          <p>
            Follow these steps in order the first time you create a campaign. Each stage explains the decision
            to make, the controls to verify, and where to complete the work.
          </p>
        </div>
        <div className="howto-flow" aria-label="Campaign workflow phases">
          <span>Understand</span>
          <Icon name="chev-right" size={13} />
          <span>Build</span>
          <Icon name="chev-right" size={13} />
          <span>Govern</span>
          <Icon name="chev-right" size={13} />
          <span>Approve</span>
          <Icon name="chev-right" size={13} />
          <span>Monitor</span>
        </div>
      </section>

      <div className="howto-start-note">
        <Icon name="info" size={18} strokeWidth={2} />
        <p>
          <strong>Before you begin:</strong> know the benefit program, business goal, intended audience, and
          campaign owner. You do not need a member list.
        </p>
      </div>

      <section className="howto-steps" aria-label="Campaign workflow steps">
        {GUIDE_STEPS.map((step) => (
          <article className="howto-step" key={step.number}>
            <div className="howto-step-rail">
              <span>{step.number}</span>
            </div>
            <div className="howto-step-content">
              <div className="howto-step-heading">
                <div>
                  <span className="howto-phase">{step.phase}</span>
                  <h3>{step.title}</h3>
                </div>
                <span className="howto-role">
                  <Icon name="user" size={13} /> {step.role}
                </span>
              </div>
              <p className="howto-description">{step.description}</p>
              <ul>
                {step.checks.map((check) => (
                  <li key={check}>{check}</li>
                ))}
              </ul>
              {step.href && (
                <Link className="howto-step-link" to={step.href}>
                  {step.action} <Icon name="chev-right" size={13} strokeWidth={2.2} />
                </Link>
              )}
            </div>
          </article>
        ))}
      </section>

      <section className="howto-boundary">
        <span>
          <Icon name="shield" size={20} strokeWidth={1.9} />
        </span>
        <div>
          <h3>Know the current system boundary</h3>
          <p>
            Today, the workflow creates, approves, and publishes a governed campaign handoff with frozen
            messages, schedules, and encrypted recipient decisions. Automatic provider delivery, verified
            delivery feedback, and enrollment or utilization attribution are not yet connected.
          </p>
        </div>
        <Link to={ACTIVATION_PATHS.methodology}>
          Read Methodology &amp; FAQ <Icon name="chev-right" size={13} />
        </Link>
      </section>
    </Shell>
  );
}
